/**
 * The user-facing "What's New" modal. Auto-pops on login if there are unread
 * entries; users can also re-open it from the sidebar bell button.
 *
 * Layout: Year/Month sidebar on the left, scrollable content on the right.
 * IntersectionObserver on month headers tracks which month is in view so the
 * sidebar highlights it.
 */

import { Filter, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  useDismiss,
  useEntries,
  useMarkRead,
  type Category,
  type Entry,
} from "../api";
import { groupEntries } from "../lib/grouping";
import { EntryCard } from "./EntryCard";
import { WhatsNewTimeline } from "./WhatsNewTimeline";

type CategoryFilter = "all" | Category;

const FILTERS: { key: CategoryFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "enhancement", label: "Enhancements" },
  { key: "fix", label: "Fixes" },
];

const DISMISS_KEY = "whats-new-dismissed";

export interface WhatsNewModalProps {
  open: boolean;
  onClose: () => void;
  /** ISO timestamp of the user's last read marker, for unread highlighting. */
  lastReadAt?: string | null;
}

export function WhatsNewModal({ open, onClose, lastReadAt }: WhatsNewModalProps) {
  const [filter, setFilter] = useState<CategoryFilter>("all");
  const [projectFilter, setProjectFilter] = useState<string | null>(null);
  const [activeMonthKey, setActiveMonthKey] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const monthRefs = useRef(new Map<string, HTMLElement>());

  const query = useEntries(
    {
      category: filter === "all" ? undefined : filter,
      project: projectFilter ?? undefined,
      limit: 200,
    },
    { enabled: open },
  );

  const lastRead = useMemo(
    () => (lastReadAt ? new Date(lastReadAt) : null),
    [lastReadAt],
  );

  // `entries` is a fresh array on each render when query.data?.items is
  // undefined. The useMemos below depend on it, but stabilizing entries
  // would just push the identity churn upstream; the memos are cheap.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const entries = query.data?.items ?? [];
  const projects = useMemo(
    () => Array.from(new Set(entries.map((e) => e.project))),
    [entries],
  );
  const sections = useMemo(
    () => groupEntries(entries, lastRead),
    [entries, lastRead],
  );

  const markRead = useMarkRead();
  const dismiss = useDismiss();

  // Track which month is in view via IntersectionObserver on the section
  // headers. Updates the sidebar highlight as the user scrolls.
  useEffect(() => {
    if (!open) return;
    const observer = new IntersectionObserver(
      (intersecting) => {
        const visible = intersecting
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const first = visible[0];
        if (first) {
          const key = first.target.getAttribute("data-month-key");
          if (key) setActiveMonthKey(key);
        }
      },
      { root: scrollRef.current, rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );

    for (const el of monthRefs.current.values()) {
      observer.observe(el);
    }
    return () => observer.disconnect();
  }, [sections, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleGotIt = async () => {
    await markRead.mutateAsync();
    try {
      window.sessionStorage.removeItem(DISMISS_KEY);
    } catch {
      /* private mode: ignore */
    }
    onClose();
  };

  const handleDismissSession = async () => {
    const token = `dismiss-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      window.sessionStorage.setItem(DISMISS_KEY, token);
    } catch {
      /* ignore */
    }
    await dismiss.mutateAsync(token);
    onClose();
  };

  const scrollToMonth = (key: string) => {
    const el = monthRefs.current.get(key);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/50 p-4">
      <div className="flex h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
        {/* Header */}
        <header className="flex items-start justify-between border-b border-border bg-card px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-brand/10 p-2">
              <Sparkles className="h-5 w-5 text-brand" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">What&apos;s New</h2>
              <p className="text-xs text-muted-foreground">
                Recent updates across the cinch_ops platform
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-card/50 px-5 py-3">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <FilterPill
                key={f.key}
                active={filter === f.key}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </FilterPill>
            ))}
          </div>
          {projects.length > 1 && (
            <div className="ml-auto flex flex-wrap items-center gap-1 text-xs">
              <span className="text-muted-foreground">Project:</span>
              <FilterPill
                active={projectFilter === null}
                onClick={() => setProjectFilter(null)}
              >
                All
              </FilterPill>
              {projects.map((p) => (
                <FilterPill
                  key={p}
                  active={projectFilter === p}
                  onClick={() => setProjectFilter(p)}
                >
                  {p}
                </FilterPill>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          <WhatsNewTimeline
            sections={sections}
            activeMonthKey={activeMonthKey}
            onMonthClick={scrollToMonth}
          />

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-4"
          >
            {query.isLoading ? (
              <div className="py-20 text-center text-sm text-muted-foreground">
                Loading…
              </div>
            ) : sections.length === 0 ? (
              <EmptyState />
            ) : (
              sections.map((year) => (
                <section key={year.year} className="mb-6 last:mb-0">
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {year.year}
                  </h3>
                  {year.months.map((month) => (
                    <div
                      key={month.key}
                      ref={(el) => {
                        if (el) monthRefs.current.set(month.key, el);
                      }}
                      data-month-key={month.key}
                      className="mb-5 last:mb-0"
                    >
                      <h4 className="sticky top-0 z-[1] -mx-5 mb-3 bg-background/95 px-5 py-1 text-sm font-semibold text-foreground backdrop-blur">
                        {month.monthLong}
                      </h4>
                      {month.dateGroups.map((dg) => (
                        <DateGroupBlock
                          key={dg.dateKey}
                          group={dg}
                          lastReadAt={lastRead}
                        />
                      ))}
                    </div>
                  ))}
                </section>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between border-t border-border bg-card px-5 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismissSession}
            disabled={dismiss.isPending}
          >
            Don&apos;t show again this session
          </Button>
          <Button onClick={handleGotIt} disabled={markRead.isPending} size="sm">
            Got it!
          </Button>
        </footer>
      </div>
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
        active
          ? "border-brand bg-brand text-white"
          : "border-border bg-background text-foreground/80 hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

function DateGroupBlock({
  group,
  lastReadAt,
}: {
  group: { dateKey: string; dateLabel: string; buildNumbers: string[]; entries: Entry[] };
  lastReadAt: Date | null;
}) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <span className="text-xs font-semibold text-foreground/80">
          {group.dateLabel}
        </span>
        {group.buildNumbers.map((b) => (
          <span
            key={b}
            className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
          >
            {b}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-2">
        {group.entries.map((entry) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            isUnread={lastReadAt ? new Date(entry.created_at) > lastReadAt : true}
          />
        ))}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center text-sm text-muted-foreground">
      <div className="rounded-full bg-muted p-4">
        <Sparkles className="h-6 w-6 text-muted-foreground" />
      </div>
      <p>No updates to show.</p>
      <p className="max-w-sm text-xs">
        New release notes will appear here when admins publish them. Filter by
        category or project at the top of this dialog.
      </p>
    </div>
  );
}

/** Returns the dismiss token stored in sessionStorage, or null. Used by the
 *  app layout to decide whether to auto-pop on login. */
export function getDismissToken(): string | null {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

export { DISMISS_KEY };
